<template>
  <v-menu
    v-model="isOpen"
    :close-on-content-click="isCompact"
    :disabled="disabled"
    location="top"
    offset="8"
  >
    <!-- The trigger lives in the parent so it keeps the card's styling/hover. -->
    <template #activator="activatorProps">
      <slot name="activator" v-bind="activatorProps" />
    </template>

    <!-- Mobile/tablet: pick a placement from a list; desktop keeps the stepper. -->
    <div
      v-if="isCompact"
      class="signature-position-menu signature-position-menu--list d-flex flex-column align-stretch py-2 overflow-y-auto"
      data-test-id="signature-position-nav"
    >
      <button
        v-for="(placeholder, index) in navigablePlaceholders"
        :key="placeholder.uid ?? index"
        class="d-flex align-center justify-space-between ga-4 px-4 py-2 w-100 bg-transparent text-grey-darken-2 text-left cursor-pointer"
        :data-test-id="`signature-position-item-${index}`"
        type="button"
        @click="selectPosition(index)"
      >
        <span class="text-body-2">
          {{ positionLabel(placeholder, index) }}
        </span>
        <v-icon aria-hidden="true" size="16"> fal fa-eye </v-icon>
      </button>
    </div>

    <div
      v-else
      class="signature-position-menu d-inline-flex align-center ga-2"
      data-test-id="signature-position-nav"
    >
      <SignIcon class="signature-position-menu__icon text-grey-darken-1" />
      <span
        class="signature-position-menu__indicator text-body-2"
        data-test-id="signature-position-indicator"
      >
        {{ activeIndex + 1 }}/{{ positionTotal }}
      </span>
      <div class="d-inline-flex align-center">
        <v-btn
          :aria-label="i18n.t('labels.previous_signature_position')"
          color="primary"
          data-test-id="signature-position-prev"
          :disabled="activeIndex <= 0"
          icon
          size="x-small"
          variant="plain"
          @click="goToPreviousPosition"
        >
          <v-icon size="small"> far fa-chevron-up </v-icon>
        </v-btn>
        <v-btn
          :aria-label="i18n.t('labels.next_signature_position')"
          color="primary"
          data-test-id="signature-position-next"
          :disabled="activeIndex >= positionTotal - 1"
          icon
          size="x-small"
          variant="plain"
          @click="goToNextPosition"
        >
          <v-icon size="small"> far fa-chevron-down </v-icon>
        </v-btn>
      </div>
    </div>
  </v-menu>
</template>

<script setup lang="ts">
import { computed, nextTick, ref, watch } from 'vue';
import { useDisplay } from 'vuetify';
import { useI18n } from 'vue-i18n';
import SignIcon from '@/components/icons/SignIcon.vue';
import type { PropType } from 'vue';
import type { Signee } from '@/types/Signee';
import type { Placeholder } from '@/composables/placement/placementTypes.ts';
import { useGlobalStore } from '@/stores/global.ts';
import {
  clamp,
  scrollToPlaceholder
} from '@/composables/placement/usePlacementUtility.ts';
import { useSigneeFilePlacements } from '@/components/create/composables/useSigneeFilePlacements.ts';

const props = defineProps({
  // Signee whose placed positions this menu steps through.
  participant: {
    type: Object as PropType<Signee>,
    required: true
  },
  // Disables the menu. The parent keeps it disabled below 2 placements on the
  // current file: a single placement is a direct jump (no stepping needed) and
  // an unplaced signee has nothing to view.
  disabled: {
    type: Boolean,
    default: false
  }
});

const emit = defineEmits<{
  // Fired after the viewer jumps to a placement (list pick on compact, or
  // stepper open/step on desktop). Compact InviteeCard uses this to close the
  // DocumentDrawer after the user picks a row.
  navigated: [];
}>();

const isOpen = defineModel<boolean>({ default: false });

const i18n = useI18n();
const { mdAndDown } = useDisplay();
const isCompact = computed(() => mdAndDown.value);
const globalStore = useGlobalStore();
const { currentFileId, placedCount, navigablePlaceholders } =
  useSigneeFilePlacements(() => props.participant);

const activeIndex = ref(0);

// Falls back to placedCount until the file's placeholders have rendered, so the
// "index/total" indicator stays correct even if scrolling is briefly a no-op.
const positionTotal = computed(
  () => navigablePlaceholders.value.length || placedCount.value
);

function positionLabel(placeholder: Placeholder, index: number) {
  // Use `{number}` (not `{n}`) — `{n}` is reserved for vue-i18n pluralization
  // and can prevent the message from resolving, which surfaces the raw key.
  return i18n.t('labels.signature_position_on_page', {
    number: index + 1,
    page: placeholder.position?.pageNumber ?? 1
  });
}

function goToPosition(index: number) {
  const positions = navigablePlaceholders.value;
  if (positions.length === 0) return;
  activeIndex.value = clamp(index, 0, positions.length - 1);
  const placeholder = positions[activeIndex.value];
  if (!placeholder?.position) return;
  globalStore.setCurrentPage(placeholder.position.pageNumber);
  scrollToPlaceholder(placeholder, true);
}

function goToNextPosition() {
  goToPosition(activeIndex.value + 1);
}

function goToPreviousPosition() {
  goToPosition(activeIndex.value - 1);
}

function selectPosition(index: number) {
  isOpen.value = false;
  // Parent closes the DocumentDrawer on compact; scroll on the next tick so
  // the stamp centers in the uncovered viewport.
  emit('navigated');
  nextTick(() => {
    goToPosition(index);
  });
}

// Desktop stepper: jump to the first position when the menu opens. Compact list
// waits for an explicit row click so opening the menu alone doesn't move the
// viewer or close the drawer. Close when the viewer switches files so the index
// never points at a stale file's placement.
watch(isOpen, (open) => {
  if (open && !isCompact.value) {
    activeIndex.value = 0;
    goToPosition(0);
  }
});

watch(currentFileId, () => {
  isOpen.value = false;
});
</script>

<style scoped lang="scss">
// Shared chrome for both stepper and list menus (utilities handle flex layout).
// Desktop stepper is content-sized; min-width is list-only so the pill doesn't
// stretch with empty space after the chevrons.
.signature-position-menu {
  padding: 6px 8px 6px 12px;
  border-radius: 10px;
  background: rgb(var(--v-theme-surface));
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.16);
}

.signature-position-menu--list {
  min-width: 180px;
  padding: 8px 0;
  // ~5 rows (each py-2 + text-body-2 line-height), then scroll.
  max-height: calc(5 * (1rem + 1.25rem));
}

// Strip native button chrome; layout/type come from Vuetify utilities.
.signature-position-menu > button {
  border: 0;

  &:hover,
  &:focus-visible {
    background: rgba(var(--v-theme-primary), 0.08);
    outline: none;
  }
}

.signature-position-menu__icon {
  width: 18px;
  height: 18px;
}

.signature-position-menu__indicator {
  color: rgb(var(--v-theme-grey-darken-2));
  font-variant-numeric: tabular-nums;
  white-space: nowrap;
}
</style>
